import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Email,
  Phone,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { usersService } from '../services/api';
import { User } from '../types';

const Users: React.FC = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');




  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      if (!orgId) {
        console.error('Organization ID not found in URL');
        setError('Organization ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('Fetching users for orgId:', orgId);
        const response = await usersService.getUsers(orgId);
        
        if (response.success && response.data) {
          console.log('Users fetched successfully:', response.data);
          setUsers(response.data.users || []);
        } else {
          console.error('Failed to fetch users');
          setError('Failed to fetch users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [orgId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };


  const filteredUsers = users.filter(user => 
    user.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.org_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort the filtered users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'name':
        aValue = a.user_name.toLowerCase();
        bValue = b.user_name.toLowerCase();
        break;
      case 'email':
        aValue = a.email.toLowerCase();
        bValue = b.email.toLowerCase();
        break;
      case 'organization':
        aValue = a.org_name.toLowerCase();
        bValue = b.org_name.toLowerCase();
        break;
      case 'lastLogin':
        aValue = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
        bValue = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField === field) {
      // Show the appropriate arrow for the current sort direction
      return (
        <Box
          component="img"
          src={sortDirection === 'asc' ? '/ChevronDownIcon.svg' : '/ChevronUpIcon.svg'}
          alt={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          sx={{ width: 18, height: 18, ml: 1, verticalAlign: 'middle' }}
        />
      );
    }
    
    // Show a default chevron for hover state
    return (
      <Box
        component="img"
        src={process.env.PUBLIC_URL + "/ChevronDownIcon.svg"}
        alt="Sort"
        sx={{ width: 18, height: 18, ml: 1, opacity: 0.5, verticalAlign: 'middle' }}
      />
    );
  };


  return (
    <Box sx={{ 
      px: 3, py: 0, // 24px horizontal padding, no vertical padding
      backgroundColor: '#FAFAFA', 
      minHeight: '100vh',
      color: 'black'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-start', 
        alignItems: 'center', 
        mb: 4 
      }}>
        <Typography 
          variant="h3" 
          sx={{ 
            color: 'rgba(0, 0, 0, 0.87)',
            fontFamily: 'Montserrat',
            fontSize: '20px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '28px',
          }}
        >
          Users
        </Typography>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'rgba(0, 0, 0, 0.6)' }}>
            Loading users...
          </Typography>
        </Box>
      ) : (
        <>
      {/* Search and Filter Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 4 
      }}>
        <TextField
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: '220px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              padding: '0 12px',
              borderRadius: '4px',
              border: '1px solid rgba(0,0,0,0.23)',
              '&:hover': {
                border: '1px solid rgba(0,0,0,0.87)',
              },
              '&.Mui-focused': {
                border: '2px solid #337a5c',
              },
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '8px 0',
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(0, 0, 0, 0.60)',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
        />
      </Box>

      {/* Separator Line */}
      <Box sx={{ 
        height: '1px', 
        backgroundColor: '#ddd', 
        mb: 2
      }} />

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#FAFAFA' }}>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 200,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  User
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('name')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 180,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('email')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Contact
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('email')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 150,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('organization')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Organization
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('organization')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell 
                sx={{ 
                  fontWeight: 500, 
                  borderBottom: '1px solid #616161',
                  padding: '16px',
                  fontFamily: 'Roboto',
                  fontSize: '14px',
                  color: 'rgba(0,0,0,0.87)',
                  letterSpacing: '0.17px',
                  lineHeight: '24px',
                  minWidth: 140,
                  cursor: 'pointer',
                  '&:hover .sort-icon': {
                    opacity: 1
                  }
                }}
                onClick={() => handleSort('lastLogin')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Last Login
                  <Box 
                    className="sort-icon"
                    sx={{ 
                      opacity: 0,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    {getSortIcon('lastLogin')}
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ 
                fontWeight: 500, 
                borderBottom: '1px solid #616161',
                padding: '16px',
                fontFamily: 'Roboto',
                fontSize: '14px',
                color: 'rgba(0,0,0,0.87)',
                letterSpacing: '0.17px',
                lineHeight: '24px',
                textAlign: 'right'
              }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
              <TableRow key={user.user_id} hover sx={{ backgroundColor: '#FAFAFA', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.87)',
                    lineHeight: '1.43',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}>
                    {user.user_name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: 14, color: 'rgba(0,0,0,0.6)' }} />
                      <Typography sx={{ 
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        color: 'rgba(0,0,0,0.87)',
                        lineHeight: '1.43',
                        whiteSpace: 'nowrap',
                      }}>
                        {user.email}
                      </Typography>
                    </Box>
                    {user.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 14, color: 'rgba(0,0,0,0.6)' }} />
                        <Typography sx={{ 
                          fontFamily: 'Montserrat',
                          fontSize: '12px',
                          color: 'rgba(0,0,0,0.6)',
                          lineHeight: '1.66',
                          letterSpacing: '0.4px'
                        }}>
                          {user.phone}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.87)',
                    lineHeight: '1.43'
                  }}>
                    {user.org_name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                  <Typography sx={{ 
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    color: 'rgba(0,0,0,0.87)',
                    lineHeight: '1.43'
                  }}>
                    {formatDate(user.last_sign_in_at)}
                  </Typography>
                </TableCell>
                <TableCell sx={{ 
                  padding: '12px 16px', 
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  textAlign: 'right'
                }}>
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={sortedUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
          lineHeight: '19px'
        }
      }}
    />
        </>
      )}
    </Box>
  );
};

export default Users;
